import React, { Component } from "react";
import { slugify } from "../../helpers";
import { locations } from "../../constants";
import Dropdown from "../Dropdown/index";
import Button from "../Button";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment, { Moment } from "moment";
import styles from "./styles.module.sass";
import isEmpty from "lodash/isEmpty";

interface BookingWidgetProps {
  city?: string;
}

interface Option {
  value: string | number;
  label: string | number;
}

interface BookingWidgetState {
  startDate: Moment;
  endDate: Moment;
  city: Option; // fully controlled: always an Option
  rooms: number | Option;
  adults: number | Option;
  children: number | Option;
}

class BookingWidget extends Component<BookingWidgetProps, BookingWidgetState> {
  // cache city options for use in constructor/render
  private cityOptions: Option[] = locations
    .map((location: any) => ({
      value: slugify(location),
      label: location,
    }))
    .filter(
      (opt: any) =>
        opt.value === "singapore" ||
        opt.value === "bali" ||
        opt.value === "kuala-lumpur"
    );

  constructor(props: BookingWidgetProps) {
    super(props);

    const initialCity = this.resolveInitialCity(props.city);

    this.state = {
      startDate: moment(),
      endDate: moment().add(1, "days"),
      city: initialCity, // set from prop or first option
      rooms: 1,
      adults: 2,
      children: 0,
    };
  }

  // Pick the correct city from prop (accepts slug or name); otherwise first option
  private resolveInitialCity = (incoming?: string): Option => {
    if (incoming && String(incoming).trim()) {
      const incomingSlug = slugify(String(incoming)).toLowerCase();
      const byValue = this.cityOptions.find(
        (o) => String(o.value).toLowerCase() === incomingSlug
      );
      if (byValue) return byValue;

      const byLabel = this.cityOptions.find(
        (o) => String(o.label).toLowerCase() === String(incoming).toLowerCase()
      );
      if (byLabel) return byLabel;
    }
    return this.cityOptions[0];
  };

  componentDidUpdate(prevProps: BookingWidgetProps) {
    if (prevProps.city !== this.props.city) {
      this.setState({
        city: this.resolveInitialCity(this.props.city),
      });
    }
  }

  getNumbers = (max: number, zeroIndex = false): Option[] => {
    return Array.from({ length: max }, (_, i) => {
      const number = zeroIndex ? i : i + 1;
      return { value: number, label: number };
    });
  };

  capitalize = (string: string): string =>
    string.charAt(0).toUpperCase() + string.slice(1);

  handleEndChange = (date: Date | null) => {
    this.setState({ endDate: moment(date) });
  };

  handleStartChange = (date: Date | null) => {
    this.setState({ startDate: moment(date) });
  };

  selectCity = (option: Option) => {
    this.setState({ city: option });
  };

  selectRooms = (option: Option) => {
    this.setState({ rooms: option });
  };

  selectAdults = (option: Option) => {
    this.setState({ adults: option });
  };

  selectChildren = (option: Option) => {
    this.setState({ children: option });
  };

  searchClick = () => {
    const { startDate, endDate, adults, children, city } = this.state;
    const adultsVal = (adults as Option).value || 2;
    const childrenVal = (children as Option).value || 0;

    const object = {
      checkInDate: startDate.format("YYYY-MM-DD"),
      checkOutDate: endDate.format("YYYY-MM-DD"),
      adults: adultsVal,
      children: childrenVal,
    };

    const bookingButton = {
      check_in_date: startDate.format("DD-MM-YYYY"),
      check_out_date: endDate.format("DD-MM-YYYY"),
      number_adults: adultsVal,
      number_children: childrenVal,
    };

    const query = (obj: Record<string, string | number>) =>
      Object.entries(obj)
        .map(([k, v]) => `${k}=${v}`)
        .join("&");

    const cityVal = typeof (city as any) === "string" ? (city as any) : city.value;

    if (cityVal === "bali") {
      window.open(
        `https://app-apac.thebookingbutton.com/properties/lloydsinnbalidirect?${query(
          bookingButton
        )}`
      );
    } else if (cityVal === "singapore") {
      window.open(`//lloydsinn.com/booking_2018.php?${query(object)}`);
    } else if (cityVal === "kuala-lumpur") {
      window.open(`//lloydsinn.com/booking_kl.php?${query(object)}`);
    }
  };

  render() {
    const { city, startDate, endDate } = this.state;

    // keep original variables to minimize unrelated changes
    const options = locations.map((location: any) => ({
      value: slugify(location),
      label: location,
    }));

    const filtered = options.filter(
      (option: any) =>
        option.value === "singapore" ||
        option.value === "bali" ||
        option.value === "kuala-lumpur"
    );

    // fully controlled: always pass a value Option
    const citySettings =
      typeof city !== "string" && !isEmpty(city) ? { value: city } : { value: this.cityOptions[0] };

    return (
      <div className={styles.booking}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="location">
            Select Lloyd&rsquo;s Inn
          </label>
          <br />
          <Dropdown
            {...citySettings}
            onChange={this.selectCity}
            options={filtered}
          />
        </div>
        <div className={styles.dates}>
          <div>
            <span className={styles.checkHeader}>Check In Date</span>
            <DatePicker
              locale="en"
              selected={startDate.toDate()}
              onChange={this.handleStartChange}
              minDate={moment().toDate()}
              inline
            />
          </div>
          <div>
            <span className={styles.checkHeader}>Check Out Date</span>
            <DatePicker
              selected={endDate.toDate()}
              onChange={this.handleEndChange}
              minDate={moment().toDate()}
              inline
            />
          </div>
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="rooms">
            Rooms
          </label>
          <br />
        <Dropdown onChange={this.selectRooms} options={this.getNumbers(4)} />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="adults">
            Adults
          </label>
          <br />
          <Dropdown onChange={this.selectAdults} options={this.getNumbers(4)} />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="children">
            Children
          </label>
          <br />
          <Dropdown
            onChange={this.selectChildren}
            options={this.getNumbers(5, true)}
          />
        </div>
        <Button className={styles.searchBtn} clickHandler={this.searchClick}>
          Search
        </Button>
      </div>
    );
  }
}

export default BookingWidget;
